/**
 * CLE-2811 — every user-visible string that names the thing a user creates in the AI contract modeling
 * tool. The code, the routes and the API call it a "run"; the product calls it a "contract model". Say
 * it here and nowhere else, so the next rename is a one-file change.
 *
 * "Model" on its own is used only where the tool's own page leaves no doubt (its buttons and toasts).
 * Headings, tabs and empty states say "contract model", so it is never mistaken for the Chatbot page's
 * chat models.
 */

/** The entity mid-sentence, for helpers that build a sentence around it. */
export const CONTRACT_MODEL = "contract model";

// ---------------------------------------------------------------------------------------------
// The list page
// ---------------------------------------------------------------------------------------------

/** The create button and the dialog it opens. */
export const NEW_CONTRACT_MODEL = "New contract model";

export const CONTRACT_MODELS_TAB = "Contract models";

export const CONTRACT_MODELS_EMPTY =
  "No contract models yet. Click “New contract model” to start.";

/**
 * CLE-2811 — a super admin's list spans every hospital, so each row names its hospital and creator, and two
 * filters above the table narrow it (`runListFilters.ts`).
 */
export const HOSPITAL_COLUMN = "Hospital";
export const CREATED_BY_COLUMN = "Created by";
/** A hospital or creator the API could not name. */
export const NO_VALUE = "—";
/** After the creator's name when it is the signed-in user. */
export const YOU_SUFFIX = " (you)";
/** The Created-by cell of the signed-in user's own run when the API could not name her. */
export const YOU_LABEL = "You";
/**
 * The Created-by filter's label for a creator the API could not name — with the start of the id, so two such
 * creators are two distinguishable entries rather than two identical "Unknown user" lines.
 */
export const unknownCreatorLabel = (createdById: string): string =>
  `Unknown user (${createdById.slice(0, 8)})`;
/** A remembered creator's entry while the list is still loading and the name is not known yet. */
export const LOADING_CREATOR = "Loading…";
export const ALL_HOSPITALS_LABEL = "All hospitals";
export const EVERYONE_LABEL = "Everyone";
export const MINE_LABEL = "Mine";
export const NO_MATCHING_CONTRACT_MODELS =
  "No contract models match these filters.";
export const CLEAR_FILTERS = "Clear filters";
/** Above the table when the API listed only the newest `limit` runs, so the filters cannot reach the older ones. */
export const runListTruncated = (limit: number): string =>
  `Showing the newest ${limit} contract models; older ones are not listed.`;

// ---------------------------------------------------------------------------------------------
// The detail page
// ---------------------------------------------------------------------------------------------

/** The short id under the heading — what you quote in a bug report — and the unnamed fallback title. */
export const modelIdLabel = (id: string): string => `Model ${id.slice(0, 8)}`;

export const LOADING_CONTRACT_MODEL = "Loading contract model…";

/** Back links: to the contract page it was opened from, else to the list. */
export const BACK_TO_CONTRACT = "Back to contract";
export const BACK_TO_CONTRACT_MODELS = "Back to contract models";

/**
 * CLE-2811 (2026-09-28) — Ani: "Instead of id show the name." When no source names a payer or provider,
 * the page says so in words rather than showing the id (`payerNames.ts`).
 */
export const UNKNOWN_PAYER = "Unknown payer";
export const UNKNOWN_PROVIDER = "Unknown provider";
/** The tooltip that keeps the id reachable for a super admin quoting it in a bug report. */
export const payerIdTitle = (id: unknown): string => `Payer id ${String(id)}`;
export const providerIdTitle = (id: unknown): string =>
  `Provider id ${String(id)}`;

// ---------------------------------------------------------------------------------------------
// Cancel and delete
// ---------------------------------------------------------------------------------------------

export const CANCEL_MODEL = "Cancel model";
export const CANCEL_MODEL_HEADER = "Cancel this model?";
export const CANCEL_MODEL_MESSAGE =
  "It stays here as a record, but nothing more will be generated for it. Model with AI on the contract will start a new contract model.";
export const MODEL_CANCELED = "Model canceled";
export const CANCEL_MODEL_FAILED = "Couldn't cancel the model";

export const DELETE_MODEL = "Delete model";
export const DELETE_MODEL_HEADER = "Delete this model?";
export const DELETE_MODEL_MESSAGE =
  "This removes the contract model and its generated output. Documents attached to the contract are kept.";
export const MODEL_DELETED = "Model deleted";
export const DELETE_MODEL_FAILED = "Couldn't delete the model";

// ---------------------------------------------------------------------------------------------
// Starting one (the "New contract model" dialog and "Model with AI" on a contract page)
// ---------------------------------------------------------------------------------------------

export const START_MODEL_FAILED = "Could not start the contract model";
export const START_MODEL_FAILED_DETAIL =
  "Something went wrong starting the contract model.";

export const NEW_MODEL_PROVIDER_HINT =
  "The contract model’s account is derived from this provider. Model one provider at a time — for an office/facility split, run it once per entity.";

/**
 * Follows the existing contract's name in the dialog's "A contract already exists…" notice. CLE-2811 — a
 * contract with attachments starts reading them the moment the contract model is created (no Generate).
 */
export const NEW_MODEL_EXISTING_CONTRACT_HINT =
  "Modeling it ties the contract model to the contract, starts reading its attachments straight away, and saves the facts back onto it.";

export const NEW_MODEL_FACTS_FIRST_HINT =
  "Start modeling uploads the documents and starts reading the contract facts straight away.";

/** CLE-2811 — the dialog's documents and instructions: the upload moved in from the run page. */
export const START_MODELING = "Start modeling";
export const MODEL_THAT_CONTRACT = "Model that contract";
export const CONTRACT_DOCUMENTS = "Contract documents";
export const DROP_FILES_HERE = "Drop files here, or";
export const NEW_MODEL_FILES_HINT =
  "The signed contract and any rate workbooks it refers to. PDF, Word, Excel, CSV or text.";
export const notAddedFiles = (names: string[]): string =>
  `Not added: ${names.join(
    ", "
  )}. The AI reads PDF, Word, Excel, CSV and text files.`;
export const INSTRUCTIONS_FOR_MODEL = "Instructions for this model (optional)";
export const INSTRUCTIONS_PLACEHOLDER =
  "e.g. Model Network P only. Use the office column, not facility.";

/** The muted line under the instructions box: what she types is added to the standing instructions. */
export const standingInstructionsStillApply = (
  type: string | null | undefined
): string => {
  if (type === "unsure")
    return "Every contract type’s standing instructions still apply; the AI uses the one for the type it finds.";
  return type
    ? `The standing instructions for ${type} contracts still apply.`
    : "The standing instructions for the contract type still apply.";
};

/** One chosen file's state in the dialog. */
export const FILE_STATUS_LABELS = {
  queued: "Queued",
  uploading: "Uploading…",
  done: "Uploaded",
  failed: "Failed",
} as const;

/** "2 of 5 uploaded, 1 failed". */
export const uploadProgressLine = (
  done: number,
  total: number,
  failed: number
): string =>
  `${done} of ${total} uploaded${failed ? `, ${failed} failed` : ""}`;

export const RETRY_FAILED_UPLOADS = "Retry failed";
export const startWithUploaded = (count: number): string =>
  `Start with ${count} uploaded`;
export const OPEN_WITHOUT_STARTING = "Open without starting";

/** The toast when the dialog is closed after the model was created but not started. */
export const MODEL_NOT_STARTED = "Contract model not started";
export const MODEL_NOT_STARTED_DETAIL =
  "It was saved with the documents that uploaded.";

/** The existing-contract notice, once she has chosen documents. */
export const EXISTING_CONTRACT_FILES_HINT =
  "The documents above are attached to the contract first.";
export const EXISTING_CONTRACT_NO_INSTRUCTIONS =
  "The instructions above are not sent when modeling that contract — give them in its conversation.";
export const EXISTING_UPLOAD_RETRY_HINT = `Some documents didn’t upload. Remove them, or click ${MODEL_THAT_CONTRACT} to try again.`;

// ---------------------------------------------------------------------------------------------
// Standing instructions
// ---------------------------------------------------------------------------------------------

export const STANDING_INSTRUCTIONS_INTRO =
  "Standing instructions are given to the AI for every contract model of that contract type, so you do not have to type them each time. When a contract model leaves the type to the AI, it gets every type’s instructions and applies the one for the type it finds.";

/** The explanation under each contract type's title. */
export const instructionExplanation = (typeLabel: string): string =>
  `Applied to every contract model built on a${
    /^[AEIOU]/i.test(typeLabel) ? "n" : ""
  } ${typeLabel} contract, in addition to what you type in the conversation.`;

// ---------------------------------------------------------------------------------------------
// The conversation and regeneration: a contract model has several passes
// ---------------------------------------------------------------------------------------------

/** The chip under her first bubble: the instructions she gave before the first generation. */
export const INITIAL_INSTRUCTIONS_CHIP = "Initial instructions";

/** The toast after a message sent while a pass is already working. */
export const QUEUED_TOAST_DETAIL = "It will run right after the current pass.";

/** Under the composer while her message waits for the pass in flight. */
export const QUEUED_COMPOSER_HINT =
  "Queued — it will run right after the current pass.";

/** The "Request a change" dialog's note on what happens mid-pass. */
export const REQUEST_CHANGE_QUEUED_HINT =
  "If it is already working, it runs right after the current pass.";

/** The conversation panel's header and composer. */
export const CONVERSATION_TITLE = "Conversation";
export const CONVERSATION_ARIA = "Conversation with the AI";
export const COMPOSER_PLACEHOLDER = "Message the AI…";
export const COMPOSER_ARIA = "Message to the AI";
export const COMPOSER_NEWLINE_HINT = "Shift+Enter for a new line";
export const SEND_MESSAGE = "Send";
export const REGENERATE = "Regenerate";
export const REGENERATE_TOOLTIP =
  "Re-read the documents and rebuild the model — e.g. after uploading a file";
export const CONVERSATION_EMPTY =
  "Tell the AI what to change — a value it misread, a page it missed, a network to leave out. It rebuilds the model and tells you what it did. Anything that doesn’t change keeps your verdict.";
export const QUESTIONS_FOR_YOU = "Questions for you";
export const REMOVE_QUEUED_MESSAGE = "Remove this queued message";
export const MESSAGE_SENDING = "sending…";
export const MESSAGE_QUEUED_CHIP = "queued";
export const MESSAGE_ABOUT_ONE_ITEM = "about one item";
export const messageOnStep = (stepTitle: string): string => `on ${stepTitle}`;
/** The thin separator where a pass started: "Version 2 · 09:24". */
export const versionSeparator = (versionNo: number, time: string): string =>
  `Version ${versionNo} · ${time}`;

/** The live reply bubble before the AI's first words arrive: "Thinking… 12 s". */
export const LIVE_REPLY_THINKING = "Thinking…";
/** Its label for screen readers. */
export const LIVE_REPLY_ARIA = "The AI's reply, as it is being written";

// ---------------------------------------------------------------------------------------------
// A pass that failed (CLE-2811): the error bubble, Retry, and a composer that is closed
// ---------------------------------------------------------------------------------------------

/** The error bubble's first line: `failed` (it stopped), `dead` (it was retried and kept failing). */
export const RUN_FAILED_TITLE = "This pass didn’t finish.";
export const RUN_DEAD_TITLE = "This pass kept failing, so it was stopped.";
/** The toggle for the raw error text under the plain-words summary. */
export const RUN_ERROR_DETAILS = "Details";
export const RUN_ERROR_HIDE_DETAILS = "Hide details";
export const RETRY = "Retry";
export const RETRY_TOOLTIP_REGENERATE =
  "Run the pass again. Anything that doesn’t change keeps your verdict.";
export const RETRY_TOOLTIP_GENERATE =
  "Read the documents and build the model again.";
export const RETRY_STARTED = "Running it again";
export const RETRY_FAILED = "Couldn’t retry";

/** `humanizeRunError`: what the worker's and clear-api's failure messages mean, in plain words. */
export const runErrorBudget = (duration: string, pass: string): string =>
  `The AI ran out of time — it didn’t finish within the ${duration} allowed for ${pass}.`;
export const RUN_ERROR_BUDGET_PASSES: Record<string, string> = {
  facts: "a facts pass",
  "facts fallback": "the slower facts path",
  full: "a full pass",
};
export const RUN_ERROR_LEASE =
  "The worker stopped responding partway through (it may have restarted), so the pass was abandoned.";
export const RUN_ERROR_EXTRACTION =
  "A document couldn’t be read, so the AI had nothing to work from.";
export const runErrorDownload = (fileName: string): string =>
  `${fileName} couldn’t be downloaded from storage.`;
export const RUN_ERROR_AI_CRASHED =
  "The AI stopped with an error before it finished.";
export const RUN_ERROR_NO_RESULT =
  "The AI finished without producing a result the tool could read.";
export const RUN_ERROR_UNKNOWN = "Something went wrong during this pass.";

/** In place of the wizard when a failed pass left nothing to review. */
export const RUN_NOTHING_MODELED =
  "Nothing was modeled — the pass failed before it produced anything. The conversation says why, and Retry runs it again.";

/** Under a closed composer (CLE-2811): a canceled contract model, or a pass that kept failing. */
export const COMPOSER_CLOSED_CANCELED_FROM_CONTRACT =
  "This contract model is canceled. To keep going, start a new one from the contract.";
export const COMPOSER_CLOSED_CANCELED = `This ${CONTRACT_MODEL} is canceled. To keep going, start a new ${CONTRACT_MODEL}.`;
export const COMPOSER_CLOSED_DEAD =
  "The last pass kept failing, so messages can’t start a new one. Use Retry above to run it again.";

/** The What Changed step before the first regeneration. */
export const NOT_REGENERATED_YET =
  "This contract model hasn’t been regenerated yet, so there’s nothing to compare against. Add an instruction or a document, then regenerate.";

// ---------------------------------------------------------------------------------------------
// The generation-progress panel (CLE-2811)
// ---------------------------------------------------------------------------------------------

/** The panel's title, by stage. */
export const PROGRESS_TITLE_FULL = `Building the ${CONTRACT_MODEL}`;
export const PROGRESS_TITLE_FACTS = "Reading the contract facts";

/** The progress strip's status chip — only for the states its spinner does not say. */
export const PROGRESS_CHIP_QUEUED = "Queued";
export const PROGRESS_CHIP_SAVING = "Saving";

/** CLE-2811 — the queued chip with the run's place in the worker's queue: "Queued — position 2". */
export const queuedChip = (position: number | null): string =>
  position != null && Number.isFinite(position) && position >= 1
    ? `${PROGRESS_CHIP_QUEUED} — position ${Math.floor(position)}`
    : PROGRESS_CHIP_QUEUED;

/** CLE-2811 — the strip's muted line when the worker left the fast facts pipeline for the legacy path. */
export const usingSlowerPath = (reason: string | null): string =>
  reason ? `Using the slower path: ${reason}` : "Using the slower path";

/** The panel's "Stop" button: cancels the model through the same confirm dialog as "Cancel model". */
export const STOP_MODEL = "Stop";
export const STOP_MODEL_TOOLTIP = `Stop generating and cancel this ${CONTRACT_MODEL}`;

/** Past twice the typical duration. */
export const TAKING_LONGER_THAN_USUAL =
  "Taking longer than usual — you can leave this page and come back.";

/** The phase timeline, in order. */
export const PHASE_LABELS = {
  queued: "Queued",
  downloading: "Downloading",
  extracting: "Reading pages",
  thinking: "AI modeling",
  saving: "Saving",
} as const;

/** Under a finished Downloading step when the previous pass's text was reused instead. */
export const PHASE_SKIPPED_REUSED = "Skipped — reused";

/** The documents list. */
export const DOCUMENTS_HEADING = "Documents";
export const DOCUMENT_WAITING = "Waiting";
export const DOCUMENT_DOWNLOADING = "Downloading…";
export const DOCUMENT_READING = "Reading…";
export const DOCUMENT_READ = "Read";
export const DOCUMENT_REUSED = "Reused from the previous pass";
/** CLE-2811 batch two — a document whose text was extracted when the file was uploaded: its row's badge. */
export const DOCUMENT_EXTRACTED_AT_UPLOAD = "Extracted at upload";
/** …and what the worker is doing then, in the phase text (and the badge's tooltip). */
export const USING_UPLOAD_TEXT =
  "Using the text extracted when the file was uploaded";
export const DOCUMENT_SCANNED_OCR = "Scanned, OCR";

/** "Live from the AI". */
export const LIVE_FROM_AI = "Live from the AI";
export const LIVE_BEFORE_THINKING =
  "The AI’s notes stream here once the documents are read.";
export const LIVE_WARMING_UP =
  "Warming up — the first words arrive within a minute.";
export const LIVE_NO_NOTES_YET = "Still working — no notes from the AI yet.";
export const LIVE_JUMP_TO_NEWEST = "Jump to the newest line";
export const LIVE_ACTIONS = "Actions";
export const liveStepLabel = (turns: number): string => `step ${turns}`;

/** "What it is looking for". */
export const LOOKING_FOR_HEADING = "What it is looking for";
export const LOOKING_FOR_HINT = "Ticks as the AI mentions each one.";

export const FACTS_CHECKLIST_LABELS = {
  signed: "Signed date",
  effective: "Effective date",
  timelyFiling: "Timely filing",
  escalators: "Escalators",
  implants: "Implants",
  multipleProcedure: "Multiple-procedure methodology",
  reimbursement: "Reimbursement schedule per plan",
} as const;

export const RATES_CHECKLIST_LABELS = {
  baseRateFiles: "Base rate files",
  rateLines: "Rate lines by service line",
  carveOuts: "Carve-outs",
  adjustmentRules: "Adjustment rules",
} as const;

/** The tips line at the foot of the panel; one every 12 seconds. */
export const PROGRESS_TIPS = [
  "You can leave this page; the model keeps building.",
  "Type in the conversation while it works — your message queues for the next pass.",
  "Standing instructions for this contract type are applied automatically.",
  "Facts are saved onto the contract only when you click Save draft.",
];

// ---------------------------------------------------------------------------------------------
// The wizard while generating (CLE-2811): skeletons and the partial preview
// ---------------------------------------------------------------------------------------------

/** The progress strip's toggle for the full panel. */
export const PROGRESS_DETAILS = "Details";
export const PROGRESS_HIDE_DETAILS = "Hide details";

/** The rail's badge on a step the pass is still building. */
export const STEP_BUILDING = "Still being built";

export const PLANS_FINDING = "Finding the plans in this contract…";
export const TERMS_READING = "Reading the contract terms…";
export const STEP_FILLS_IN = "This step fills in when the pass finishes.";

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);
const failedSuffix = (failed: number) => (failed ? `, ${failed} failed` : "");

export const allPlansRead = (total: number): string =>
  total === 1 ? "The plan is read" : `All ${total} plans read`;

/** "Extracting Medflex, SuperMed… 2 of 4 done". */
export const extractingPlansLine = (
  names: string[],
  done: number,
  total: number,
  failed: number
): string =>
  `Extracting ${names.join(", ")}… ${done} of ${total} done${failedSuffix(
    failed
  )}`;

/** "4 plans found · 2 of 4 done". */
export const plansFoundLine = (
  done: number,
  total: number,
  failed: number
): string =>
  `${total} ${plural(
    total,
    "plan",
    "plans"
  )} found · ${done} of ${total} done${failedSuffix(failed)}`;

/** On every preview card. */
export const PREVIEW_NOT_SAVED = "Preview — not saved yet";
export const PREVIEW_STATUS_LABELS = {
  pending: "Waiting",
  extracting: "Extracting…",
  done: "Read",
  failed: "Couldn’t read",
} as const;
export const PREVIEW_FAILED_DETAIL = "The AI could not read this plan’s terms.";
export const PREVIEW_CONTRACT_FACTS = "Contract facts";
export const PREVIEW_OPEN_QUESTIONS = "Open questions";
export const previewContractType = (type: string): string =>
  `Contract type: ${type}`;

/** Under the Lines of Business cards while the pass runs. */
export const LOB_CHOOSE_AFTER =
  "You can choose which lines of business to model once the pass finishes.";

/** The strip's typical duration: "usually ~1½ min". */
export const usuallyAbout = (duration: string): string => `usually ${duration}`;

// ---------------------------------------------------------------------------------------------
// Facts first (CLE-2811): the facts are saved while the pass still reads the schedules
// ---------------------------------------------------------------------------------------------

/** The strip's title once the facts are saved: "Facts ready — extracting schedules 2 of 4". */
export const factsReadyTitle = (
  done: number,
  total: number,
  failed = 0
): string =>
  total > 0
    ? `Facts ready — extracting schedules ${done} of ${total}${failedSuffix(
        failed
      )}`
    : "Facts ready — extracting schedules";

/** In place of an empty reimbursement slot while its plan's schedule is still being read. */
export const SCHEDULE_EXTRACTING = "Extracting the schedule…";

/** …when the worker reports it could not read that plan. */
export const SCHEDULE_NOT_READ = "The AI couldn’t read this plan’s schedule.";

/** The amber chip on a term whose schedule arrived after she had confirmed its facts. */
export const SCHEDULE_ADDED_CHIP = "Schedule added — re-confirm";

// ---------------------------------------------------------------------------------------------
// Save draft (CLE-2811, 2026-09-28): the facts run's last button — Ani: "It should be Save Draft"
// ---------------------------------------------------------------------------------------------

export const SAVE_DRAFT_TOOLTIP =
  "Confirms every contract term you haven’t rejected or overridden, with your edits, and saves each onto its contract — empty fields only. A term that still needs input is skipped.";
export const SAVE_DRAFT_FAILED = "Couldn’t save the draft";
/** On a term Save draft skipped: its payer has several contracts, and which one is her call. */
export const pickContractAttention = (payerName: string): string =>
  `Not saved: this provider has several ${payerName} contracts. Pick one in the save panel below.`;
/** The payer group's tag for such a term. */
export const PICK_A_CONTRACT = "Pick a contract";

// ---------------------------------------------------------------------------------------------
// The contract page (CLE-2811): the latest contract model's banner, its buttons, and the upload
// ---------------------------------------------------------------------------------------------

export const MODEL_WITH_AI = "Model with AI";
export const OPEN_LATEST_MODEL = "Open latest model";
export const START_NEW_MODEL = "Start a new model";
/** The banner's link to the contract model. */
export const OPEN_MODEL = "Open";

/** The banner, by the latest contract model's state. */
export const CONTRACT_MODEL_READING = "AI is reading this contract…";
export const CONTRACT_MODEL_FACTS_READY =
  "Facts ready — schedules still extracting";
export const contractModelFound = (items: number): string =>
  `AI found the contract facts — review and save (${items} ${plural(
    items,
    "item",
    "items"
  )})`;
export const CONTRACT_MODEL_FOUND_NOTHING =
  "The AI finished without finding any contract facts";
export const contractModelFailed = (reason: string): string =>
  `The AI pass failed: ${reason}`;
/**
 * CLE-2811 (2026-09-28) — the latest model is one on ANOTHER contract that saved a contract term into this
 * one (`origin: "saved_here"`): it created this contract, or saved an off-payer term into it. "Saved"
 * covers both. A standalone model is on no contract, so it does not say "another contract".
 */
export const CONTRACT_MODEL_SAVED_HERE =
  "Saved from an AI model on another contract";
export const CONTRACT_MODEL_SAVED_HERE_STANDALONE = "Saved from an AI model";

/** The attachment upload dialog. */
export const UPLOAD_ATTACHMENTS = "Upload files";
export const UPLOAD_ATTACHMENTS_HEADER = "Upload attachments";
export const UPLOAD_CHOOSE_FILES = "Choose files";
export const UPLOAD_SUBMIT = "Upload";
export const UPLOAD_CANCEL = "Cancel";
export const UPLOAD_REMOVE_FILE = "Remove this file";
export const UPLOAD_NO_FILES =
  "No files chosen yet. PDF, Word, Excel or images, up to 50 MB each.";
export const EXTRACT_WITH_AI_LABEL =
  "Extract contract facts with AI after upload";
export const EXTRACT_WITH_AI_HINT = `Starts a ${CONTRACT_MODEL} that reads this contract’s attachments. Nothing is saved onto the contract until you review it.`;
export const uploadedFiles = (count: number): string =>
  `Uploaded ${count} ${plural(count, "file", "files")}`;
export const uploadFailedFiles = (names: string[]): string =>
  `Couldn’t upload ${names.join(", ")}.`;
export const UPLOAD_FAILED = "Upload failed";
export const AI_STARTED_READING = "AI started reading the contract";
export const AI_DID_NOT_START = "The AI didn’t start reading the contract";
export const AI_DID_NOT_START_DETAIL = `Use ${MODEL_WITH_AI} to start a ${CONTRACT_MODEL}.`;
export const AI_NOTHING_TO_READ =
  "It reads PDF, Word, Excel, CSV and text files, and none were uploaded.";
